import React from 'react';
import './CandidateRegistrationPage.scss';
import { Link, useLocation } from 'react-router-dom';
import CandidateForm from 'forms/forms/CandidateForm';
import { ICandidateForm } from 'store/reducers/elections/types';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
    asyncCreateCandidate,
    asyncFetchMyCandidate,
    asyncFetchRegistrationStatus,
    asyncUpdateCandidate,
} from 'store/reducers/elections/thunks';
import { asyncFetchUser } from 'store/reducers/user/thunks';
import LoginModal from 'forms/modals/LoginModal';
import LoginForm from 'forms/forms/LoginForm';
import { showToast } from 'components/redesign/Toast';
import CandidatePreview, { buildPreviewCandidate } from './CandidatePreview';

type Step = 'form' | 'preview';

const EMPTY_FORM: ICandidateForm = {
    picture: undefined,
    vk_nick: '',
    telegram_nick: '',
    program_text: '',
    description_text: '',
};

function backendErrorMessages(error: unknown): string[] {
    if (typeof error === 'string') return [error];
    if (!error || typeof error !== 'object') return [];
    const messages: string[] = [];
    Object.entries(error as Record<string, unknown>).forEach(
        ([field, value]) => {
            if (Array.isArray(value)) {
                value.forEach(item => messages.push(`${field}: ${item}`));
            } else if (value) {
                messages.push(`${field}: ${value}`);
            }
        }
    );
    return messages;
}

function CandidateRegistrationPage(): React.ReactElement {
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const {
        registrationStatus,
        registrationStatusLoading,
        registrationStatusError,
    } = useAppSelector(state => state.electionsStore);
    const { user } = useAppSelector(state => state.userStore);
    React.useEffect(() => {
        if (user) dispatch(asyncFetchRegistrationStatus());
    }, [dispatch, user]);

    const [submitted, setSubmitted] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [loadingEdit, setLoadingEdit] = React.useState(false);
    const [existingPictureUrl, setExistingPictureUrl] = React.useState('');
    const [loginOpen, setLoginOpen] = React.useState(false);
    const onLoginSuccess = React.useCallback(async () => {
        setLoginOpen(false);
        await dispatch(asyncFetchUser()).unwrap();
    }, [dispatch]);

    const [step, setStep] = React.useState<Step>('form');
    const [form, setForm] = React.useState<ICandidateForm>(EMPTY_FORM);
    const [submitting, setSubmitting] = React.useState(false);
    const [pictureUrl, setPictureUrl] = React.useState('');
    React.useEffect(() => {
        if (!form.picture) {
            setPictureUrl('');
            return undefined;
        }
        const url = URL.createObjectURL(form.picture);
        setPictureUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [form.picture]);

    const previewCandidate = React.useMemo(
        () =>
            buildPreviewCandidate(form, pictureUrl || existingPictureUrl, user),
        [form, pictureUrl, existingPictureUrl, user]
    );

    const onEdit = React.useCallback(() => {
        if (loadingEdit) return;
        setLoadingEdit(true);
        dispatch(asyncFetchMyCandidate())
            .unwrap()
            .then(candidate => {
                setForm({
                    picture: undefined,
                    vk_nick: candidate.vk_nick || '',
                    telegram_nick: candidate.telegram_nick || '',
                    program_text: candidate.program_text,
                    description_text: candidate.description_text,
                });
                setExistingPictureUrl(candidate.picture || '');
                setStep('form');
                setEditing(true);
            })
            .catch(error => {
                const messages = backendErrorMessages(error);
                showToast(
                    messages[0] || 'Не удалось загрузить анкету',
                    'warning'
                );
            })
            .finally(() => setLoadingEdit(false));
    }, [dispatch, loadingEdit]);

    const onSubmit = React.useCallback(() => {
        if (submitting) return;
        setSubmitting(true);
        (editing
            ? dispatch(asyncUpdateCandidate(form)).unwrap()
            : dispatch(asyncCreateCandidate(form)).unwrap()
        )
            .then(() => {
                setSubmitting(false);
                setSubmitted(true);
                setSaved(editing);
                setEditing(false);
            })
            .catch(error => {
                setSubmitting(false);
                const messages = backendErrorMessages(error);
                showToast(
                    messages[0] ||
                        'Не удалось отправить анкету — попробуй ещё раз',
                    'warning'
                );
            });
    }, [dispatch, editing, form, submitting]);

    const isSubmitted =
        submitted || (Boolean(user) && registrationStatus === 'submitted');
    const registrationClosed = Boolean(user) && registrationStatus === 'closed';
    const forbidden = Boolean(user) && registrationStatus === 'forbidden';
    const showGuest = !user;
    const showStatusError = Boolean(user) && registrationStatusError;
    const showStatusLoading =
        Boolean(user) &&
        !showStatusError &&
        (registrationStatusLoading || registrationStatus === null);
    const showForm =
        Boolean(user) &&
        ((editing && (registrationStatus === 'submitted' || submitted)) ||
            (!isSubmitted && registrationStatus === 'available'));

    return (
        <div className="rd-container rd-sections">
            {isSubmitted && !editing && (
                <section className="rd-block rd-registration-done">
                    <h1 className="rd-h1">
                        {saved ? 'Изменения сохранены' : 'Заявка отправлена'}
                    </h1>
                    <p className="rd-registration-done__hint">
                        Анкета уже у избиркома Студенческого совета. Удачи на
                        выборах!
                    </p>
                    <button
                        className="rd-btn rd-btn--green"
                        type="button"
                        onClick={onEdit}
                        disabled={loadingEdit}
                    >
                        {loadingEdit ? 'Загружаем…' : 'Редактировать анкету'}
                    </button>
                    <Link className="rd-btn rd-btn--dark" to={`/${search}`}>
                        На главную
                    </Link>
                </section>
            )}

            {showGuest && (
                <section className="rd-block rd-registration-closed">
                    <h1 className="rd-h1">Войди, чтобы подать анкету</h1>
                    <p className="rd-registration-closed__hint">
                        После входа откроется анкета кандидата.
                    </p>
                    <button
                        className="rd-btn rd-btn--dark"
                        type="button"
                        onClick={() => setLoginOpen(true)}
                    >
                        Войти в систему
                    </button>
                </section>
            )}

            {showStatusLoading && (
                <section
                    className="rd-block rd-registration-closed"
                    role="status"
                >
                    <h1 className="rd-h1">Загружаем анкету</h1>
                    <p className="rd-registration-closed__hint">
                        Проверяем статус заявки…
                    </p>
                </section>
            )}

            {showStatusError && (
                <section
                    className="rd-block rd-registration-closed"
                    role="alert"
                >
                    <h1 className="rd-h1">Не удалось загрузить анкету</h1>
                    <p className="rd-registration-closed__hint">
                        Проверь соединение и попробуй ещё раз.
                    </p>
                    <button
                        className="rd-btn rd-btn--dark"
                        type="button"
                        onClick={() => dispatch(asyncFetchRegistrationStatus())}
                    >
                        Повторить
                    </button>
                </section>
            )}

            {forbidden && (
                <section className="rd-block rd-registration-closed">
                    <h1 className="rd-h1">Подача анкеты недоступна</h1>
                    <p>
                        Твой профиль не соответствует условиям регистрации
                        кандидатов.
                    </p>
                    <Link className="rd-btn rd-btn--dark" to={`/${search}`}>
                        Смотреть выборы
                    </Link>
                </section>
            )}

            {registrationClosed && (
                <section className="rd-block rd-registration-closed">
                    <h1 className="rd-h1">Регистрация кандидатов закрыта</h1>
                    <p className="rd-registration-closed__hint">
                        Сейчас нет выборов, на которые ты можешь подать анкету.
                    </p>
                    <Link className="rd-btn rd-btn--dark" to={`/${search}`}>
                        Смотреть выборы
                    </Link>
                </section>
            )}

            {showForm && (
                <>
                    <section
                        className={`rd-block${
                            step === 'preview' ? ' rd-hidden' : ''
                        }`}
                    >
                        <h1 className="rd-h1">
                            {editing
                                ? 'Редактировать анкету'
                                : 'Анкета для кандидатов'}
                        </h1>
                        <CandidateForm
                            form={form}
                            setForm={setForm}
                            existingPictureUrl={existingPictureUrl}
                            editing={editing}
                            onValid={() => setStep('preview')}
                        />
                    </section>

                    {step === 'preview' && (
                        <CandidatePreview
                            candidate={previewCandidate}
                            editing={editing}
                            submitting={submitting}
                            onBack={() => setStep('form')}
                            onSubmit={onSubmit}
                        />
                    )}
                </>
            )}

            <LoginModal
                open={loginOpen}
                onCancel={() => setLoginOpen(false)}
                header="Авторизация"
                subHeader="Откроет доступ к анкете кандидата"
                form={<LoginForm onSuccess={onLoginSuccess} />}
            />
        </div>
    );
}

export default React.memo(CandidateRegistrationPage);
